import React, { useCallback, useEffect, useState } from "react"
import { useHistory } from "react-router-dom"
import { gql, useApolloClient, useMutation, useQuery } from "@apollo/client"

import { Layout, Fieldset } from "components"
import { Form, Formik } from "formik"

import * as yup from "yup"

import formatDate from "date-fns/format"
import maxDate from "date-fns/max"
import minDate from "date-fns/min"
import parseISO from "date-fns/parseISO"

import uniqBy from "lodash/uniqBy"
//

const CREATE_TRIP = gql`
  mutation CreateTrip($input: CreateTripInput!) {
    createTrip(input: $input) {
      id
      name
      url
      code
      startDate
      endDate
      photoURL
      image {
        url
      }

      agents {
        displayName
      }
      leaders {
        displayName
      }
    }
  }
`

const CREATE_PRODUCTS = gql`
  mutation CreateProducts($input: CreateProductsInput!) {
    createProducts(input: $input) {
      id
      name
      description
      category
      price
      availability
      quantity
      index
    }
  }
`

const createTripSchema = yup.object().shape({
  name: yup.string().required("Name is required"),
  code: yup.number().required("Code is required"),
  startDate: yup.date().required("Start date is required"),
  endDate: yup.date().required("End date is required"),
  // destinations: yup
  //   .array("You must have at least one destination")
  //   .required("You must have at least one destination")
  //   .min(1, "You must have at least one destination")
  //   .nullable(),

  image: yup.object().required("Cover image is required").nullable(),

  legal: yup.object().shape({
    contract: yup.object().required("Contract is required").nullable(),
  }),

  account: yup.object().required("Account is required").nullable(),

  agency: yup.object().required("Agency is required").nullable(),

  // agents: yup
  //   .array("You must have at least one agent")
  //   .required("You must have at least one agent")
  //   .min(1, "You must have at least one agent")
  //   .nullable(),

  payment: yup.object().shape({
    price: yup
      .number()
      .nullable()
      .required("Base price is required")
      .min(0, "Based price can't be 0"),

    deposits: yup
      .array()
      .of(
        yup.object({
          amount: yup
            .number()
            .typeError("Amount must be a number")
            .nullable()
            .when("$index", (_, schema, { index, parent }) => {
              return parent.amount
                ? schema
                    .min(1, "Amount more then 0")
                    .required("Amount is required")
                : schema
            }),
          dueDate: yup
            .date()
            .when("$index", (_, schema, { index }) => {
              return index === 0
                ? schema
                : schema.required("Due date is required")
            })
            .nullable(),
        }),
      )
      .test(
        "sales-sum-test",
        "Deposit sum is more or equal to base price",
        function (deposits, { parent }) {
          const total = parent?.price
          const sum = deposits.reduce(
            (sum, item) => (sum += item.amount ? item.amount : 0),
            0,
          )

          return total >= sum
        },
      ),
  }),
})

const IndividualCreatePage = () => {
  const history = useHistory()
  const [createTrip] = useMutation(CREATE_TRIP)
  const [createProducts] = useMutation(CREATE_PRODUCTS)

  const formikProps = {
    initialValues: {
      name: "",
      code: "",
      startDate: "",
      endDate: "",
      destinations: null,

      // image: null,

      legal: {
        contract: null,
      },

      account: { label: "Alpine Adventures", value: "ALPINE_ADVENTURES" },

      agency: null,
      agents: null,
    },
    validateOnBlur: false,
    validateOnChange: false,
    // validationSchema: createTripSchema,
    onSubmit: async ({ products, template, ...values }, actions) => {
      try {
        const input = {
          type: "INDIVIDUAL",
          ...values,
          destinations: values?.destinations?.map((_) => _.value),
          // image: {
          //   id: values?.image?.id,
          //   name: values?.image?.name,
          //   url: values?.image?.url,
          // },

          // payment: {
          //   ...values.payment,
          //   deposits: values?.payment?.deposits.map((part) => {
          //     const { id, ...rest } = part
          //     return rest
          //   }),
          // },

          legal: {
            ...values.legal,
            contract: { ...values.legal.contract.value },
          },

          account: values?.account?.value,
          agency: { ...values?.agency?.value },
          agents: values?.agents?.map((_) => _?.value),
          // leaders: values?.leaders?.map((_) => _?.value),
          leaders: null,
        }

        const { data } = await createTrip({ variables: { input } })

        const { id: tripId } = data.createTrip

        await createProducts({ variables: { input: { tripId, products } } })

        history.push(`/individual/${tripId}`)

        return
      } catch (e) {
        const errors = e.graphQLErrors.map((_) => _)
        actions.setFieldError("server", errors)
      }
    },
  }

  return (
    <Layout title="Create Individual">
      <header className="py-8 bg-gray-800 text-gray-100">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          <h1 className="font-black text-3xl">Create Individual</h1>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pb-32">
        <div className="relative -mt-24 z-30 flex items-start space-x-4">
          <div className="w-8/12 bg-white rounded-lg shadow-lg">
            <header className="p-6 border-b-2 border-gray-100 flex justify-between items-center">
              <h3 className="text-xl leading-none font-medium text-gray-900">
                Create Individual
              </h3>
            </header>

            <Formik {...formikProps}>
              {({ values, errors, isSubmitting, setValues }) => {
                return (
                  <Form className="p-6">
                    <Template
                      template={values?.template}
                      onTemplate={(data) => setValues({ ...values, ...data })}
                    />

                    <TripGeneralForm />
                    <TripLegalForm />
                    <TripOrganisationForm />

                    <div className="py-3">
                      {errors?.server?.map(({ code, message }) => (
                        <div role="alert">
                          <div className="bg-red-500 text-white font-bold rounded-t px-4 py-2">
                            {code}
                          </div>
                          <div className="border border-t-0 border-red-400 rounded-b bg-red-100 px-4 py-3 text-red-700">
                            <p>{message}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <FormNavigation isSubmitting={isSubmitting} />
                  </Form>
                )
              }}
            </Formik>
          </div>
        </div>
      </main>
    </Layout>
  )
}

export default IndividualCreatePage

const Template = ({ template, onTemplate }) => {
  useEffect(() => {
    if (template) {
      const { Customer, ItineraryItem, PackageName } = template.value

      const productMock = [
        {
          AdultCount: "0",
          ArrivalDate: "2016-02-20T00:00:00",
          CCType: "  ",
          ChildCount: "0",
          CommissionBillDate: "2016-03-21T00:00:00",
          CommissionDueDate: "2016-04-30T00:00:00",
          CommissionRate: "0.0000",
          CommissionTotal: "0.0000",
          CreateDateTime: "2015-09-09T13:55:59.607",
          DateCancelled: "1900-01-01T00:00:00",
          DateSaved: "2015-09-09T14:00:54.097",
          DepartureDate: "2016-02-20T00:00:00",
          IATACommissionDueDate: "1900-01-01T00:00:00",
          IATACommissionRate: "0.00000000",
          IATACommissionTotal: "0",
          IsCRSPassThrough: "1",
          ItineraryId: "10204794",
          ItineraryItemId: "2",
          LastChange: "2015-09-28T10:41:40.410",
          LocationId: "1874224",
          LocationName: "Alpine Adventures - Anywhere Adventures",
          LoggedAgentForItem: "igerlach",
          NetPrice: "0.0000",
          OrigNetPrice: "0.0000",
          OrigPrice: "208.9400",
          OrigTaxesFees: "0.0000",
          PackageId: "0",
          Price: "208.9400",
          PriorNetPrice: "0.0000",
          PriorPrice: "0.0000",
          PriorTaxesFees: "0.0000",
          ProductCategoryId: "110",
          ProductCategoryName: "Booking Fee",
          ProductId: "1",
          ProductName: "Thank you for booking with Alpine Adventures",
          Quantity: "1",
          ResDate: "2015-09-28T10:41:40.410",
          ResellerCollects: "1",
          SalesCommissionBillDate: "2016-03-21T00:00:00",
          SalesCommissionDueDate: "2016-04-30T00:00:00",
          SalesCommissionRate: "0.0000",
          SalesCommissionTotal: "0.0000",
          StatusCode: "2",
          SuperCategoryID: "10",
          SuperCategoryName: "Misc",
          SupplierConfirmationNumber: "",
          SupplierId: "1874224",
          SupplierName: "Alpine Adventures - Anywhere Adventures",
          TaxesFees: "0.0000",
          TotalNights: "0",
        },
        {
          AdultCount: "1",
          ArrivalDate: "2016-02-20T00:00:00",
          CCType: "  ",
          ChildCount: "0",
          CommissionBillDate: "2016-03-21T00:00:00",
          CommissionDueDate: "2016-04-30T00:00:00",
          CommissionRate: "0.2000",
          CommissionTotal: "88.1800",
          CreateDateTime: "2015-09-09T13:58:18.160",
          DateCancelled: "1900-01-01T00:00:00",
          DateSaved: "2015-09-09T14:00:54.097",
          DepartureDate: "2016-02-20T00:00:00",
          IATACommissionDueDate: "1900-01-01T00:00:00",
          IATACommissionRate: "0.00000000",
          IATACommissionTotal: "0",
          IsCRSPassThrough: "1",
          ItineraryId: "10204794",
          ItineraryItemId: "3",
          LastChange: "2021-07-09T10:12:13.490",
          LocationId: "2011473",
          LocationName: "Altitude Espace Taxi Val d'Isere ",
          LoggedAgentForItem: "igerlach",
          NetPrice: "352.7400",
          OrigNetPrice: "352.7400",
          OrigPrice: "440.9200",
          OrigTaxesFees: "0.0000",
          PackageId: "0",
          Price: "440.9200",
          PriorNetPrice: "0.0000",
          PriorPrice: "0.0000",
          PriorTaxesFees: "0.0000",
          ProductCategoryId: "147",
          ProductCategoryName: "Ground Transportation",
          ProductId: "67",
          ProductName:
            "One way private transportation Bourg Saint Maurice-Val d'Isere/Tignes, 1-2 pax (6AM-9PM)",
          Quantity: "1",
          ResDate: "2015-09-28T10:41:40.463",
          ResellerCollects: "1",
          SalesCommissionBillDate: "2016-03-21T00:00:00",
          SalesCommissionDueDate: "2016-04-30T00:00:00",
          SalesCommissionRate: "0.1800",
          SalesCommissionTotal: "77.5984",
          StatusCode: "2",
          SuperCategoryID: "3",
          SuperCategoryName: "Shuttle",
          SupplierConfirmationNumber: "",
          SupplierId: "2011473",
          SupplierName: "Altitude Espace Taxi Val d'Isere ",
          TaxesFees: "0.0000",
          TotalNights: "0",
        },
        {
          AdultCount: "1",
          ArrivalDate: "2016-02-27T00:00:00",
          CCType: "  ",
          ChildCount: "0",
          CommissionBillDate: "2016-03-28T00:00:00",
          CommissionDueDate: "2016-04-30T00:00:00",
          CommissionRate: "0.2000",
          CommissionTotal: "88.1800",
          CreateDateTime: "2015-09-09T13:58:24.170",
          DateCancelled: "1900-01-01T00:00:00",
          DateSaved: "2015-09-09T14:00:54.097",
          DepartureDate: "2016-02-27T00:00:00",
          IATACommissionDueDate: "1900-01-01T00:00:00",
          IATACommissionRate: "0.00000000",
          IATACommissionTotal: "0",
          IsCRSPassThrough: "1",
          ItineraryId: "10204794",
          ItineraryItemId: "4",
          LastChange: "2021-07-09T10:12:11.363",
          LocationId: "2011473",
          LocationName: "Altitude Espace Taxi Val d'Isere ",
          LoggedAgentForItem: "igerlach",
          NetPrice: "352.7400",
          OrigNetPrice: "352.7400",
          OrigPrice: "440.9200",
          OrigTaxesFees: "0.0000",
          PackageId: "0",
          Price: "440.9200",
          PriorNetPrice: "0.0000",
          PriorPrice: "0.0000",
          PriorTaxesFees: "0.0000",
          ProductCategoryId: "147",
          ProductCategoryName: "Ground Transportation",
          ProductId: "63",
          ProductName:
            "One way private transportation Val d'Isere/Tignes-Bourg Saint Maurice, 1-2 pax (6AM-9PM)",
          Quantity: "1",
          ResDate: "2015-09-28T10:41:40.490",
          ResellerCollects: "1",
          SalesCommissionBillDate: "2016-03-28T00:00:00",
          SalesCommissionDueDate: "2016-04-30T00:00:00",
          SalesCommissionRate: "0.1800",
          SalesCommissionTotal: "77.5984",
          StatusCode: "2",
          SuperCategoryID: "3",
          SuperCategoryName: "Shuttle",
          SupplierConfirmationNumber: "",
          SupplierId: "2011473",
          SupplierName: "Altitude Espace Taxi Val d'Isere ",
          TaxesFees: "0.0000",
          TotalNights: "0",
        },
        {
          AdultCount: "2",
          ArrivalDate: "2016-02-20T00:00:00",
          CCType: "  ",
          ChildCount: "2",
          CommissionBillDate: "2016-03-28T00:00:00",
          CommissionDueDate: "2016-04-30T00:00:00",
          CommissionRate: "0.2000",
          CommissionTotal: "750.8900",
          CreateDateTime: "2015-09-10T14:55:37.160",
          DateCancelled: "1900-01-01T00:00:00",
          DateSaved: "2015-09-09T14:00:54.097",
          DepartureDate: "2016-02-27T00:00:00",
          IATACommissionDueDate: "1900-01-01T00:00:00",
          IATACommissionRate: "0.00000000",
          IATACommissionTotal: "0",
          IsCRSPassThrough: "1",
          ItineraryId: "10204794",
          ItineraryItemId: "6",
          LastChange: "2015-09-28T10:41:40.507",
          LocationId: "1900293",
          LocationName: "Montana Plein Sud",
          LoggedAgentForItem: "igerlach",
          NetPrice: "3003.7700",
          OrigNetPrice: "3003.7700",
          OrigPrice: "3754.6600",
          OrigTaxesFees: "0.0000",
          PackageId: "0",
          Price: "3754.6600",
          PriorNetPrice: "0.0000",
          PriorPrice: "0.0000",
          PriorTaxesFees: "0.0000",
          ProductCategoryId: "1",
          ProductCategoryName: "Lodging",
          ProductId: "1",
          ProductName: "2 Bedroom Apartment",
          Quantity: "1",
          ResDate: "2015-09-28T10:41:40.507",
          ResellerCollects: "1",
          SalesCommissionBillDate: "2016-03-28T00:00:00",
          SalesCommissionDueDate: "2016-04-30T00:00:00",
          SalesCommissionRate: "0.1800",
          SalesCommissionTotal: "660.7832",
          StatusCode: "1",
          SuperCategoryID: "1",
          SuperCategoryName: "Lodging",
          SupplierConfirmationNumber: "",
          SupplierId: "1900293",
          SupplierName: "Montana Plein Sud",
          TaxesFees: "0.0000",
          TotalNights: "7",
        },
        {
          AdultCount: "2",
          ArrivalDate: "2016-02-21T00:00:00",
          CCType: "  ",
          ChildCount: "0",
          CommissionBillDate: "2016-03-27T00:00:00",
          CommissionDueDate: "2016-04-30T00:00:00",
          CommissionRate: "0.2200",
          CommissionTotal: "50.0000",
          CreateDateTime: "2015-09-10T14:58:48.770",
          DateCancelled: "1900-01-01T00:00:00",
          DateSaved: "2015-09-09T14:00:54.097",
          DepartureDate: "2016-02-26T00:00:00",
          IATACommissionDueDate: "1900-01-01T00:00:00",
          IATACommissionRate: "0.00000000",
          IATACommissionTotal: "0",
          IsCRSPassThrough: "1",
          ItineraryId: "10204794",
          ItineraryItemId: "7",
          LastChange: "2018-05-08T17:26:37.310",
          LocationId: "2022626",
          LocationName: "Alpine Adventures Europe Supplier",
          LoggedAgentForItem: "igerlach",
          NetPrice: "182.0000",
          OrigNetPrice: "182.0000",
          OrigPrice: "232.0000",
          OrigTaxesFees: "0.0000",
          PackageId: "0",
          Price: "232.0000",
          PriorNetPrice: "0.0000",
          PriorPrice: "0.0000",
          PriorTaxesFees: "0.0000",
          ProductCategoryId: "1061",
          ProductCategoryName: "Ski Rental",
          ProductId: "835",
          ProductName: "6 Day Adult Premium Ski Pack",
          Quantity: "2",
          ResDate: "2015-09-28T10:41:40.547",
          ResellerCollects: "1",
          SalesCommissionBillDate: "2016-03-27T00:00:00",
          SalesCommissionDueDate: "2016-04-30T00:00:00",
          SalesCommissionRate: "0.1900",
          SalesCommissionTotal: "44.0000",
          StatusCode: "2",
          SuperCategoryID: "6",
          SuperCategoryName: "Equipment",
          SupplierConfirmationNumber: "",
          SupplierId: "2022626",
          SupplierName: "Alpine Adventures Europe Supplier",
          TaxesFees: "0.0000",
          TotalNights: "0",
        },
        {
          AdultCount: "2",
          ArrivalDate: "2016-02-21T00:00:00",
          CCType: "  ",
          ChildCount: "0",
          CommissionBillDate: "2016-03-27T00:00:00",
          CommissionDueDate: "2016-04-30T00:00:00",
          CommissionRate: "0.2500",
          CommissionTotal: "15.0000",
          CreateDateTime: "2015-09-10T15:00:59.827",
          DateCancelled: "1900-01-01T00:00:00",
          DateSaved: "2015-09-09T14:00:54.097",
          DepartureDate: "2016-02-26T00:00:00",
          IATACommissionDueDate: "1900-01-01T00:00:00",
          IATACommissionRate: "0.00000000",
          IATACommissionTotal: "0",
          IsCRSPassThrough: "1",
          ItineraryId: "10204794",
          ItineraryItemId: "8",
          LastChange: "2018-05-08T17:25:27.337",
          LocationId: "2022626",
          LocationName: "Alpine Adventures Europe Supplier",
          LoggedAgentForItem: "igerlach",
          NetPrice: "45.0000",
          OrigNetPrice: "45.0000",
          OrigPrice: "60.0000",
          OrigTaxesFees: "0.0000",
          PackageId: "0",
          Price: "60.0000",
          PriorNetPrice: "0.0000",
          PriorPrice: "0.0000",
          PriorTaxesFees: "0.0000",
          ProductCategoryId: "1061",
          ProductCategoryName: "Ski Rental",
          ProductId: "836",
          ProductName: "6 Day Kids Ski Rental Pack with helmet",
          Quantity: "1",
          ResDate: "2015-09-28T10:41:40.557",
          ResellerCollects: "1",
          SalesCommissionBillDate: "2016-03-27T00:00:00",
          SalesCommissionDueDate: "2016-04-30T00:00:00",
          SalesCommissionRate: "0.2200",
          SalesCommissionTotal: "13.2000",
          StatusCode: "2",
          SuperCategoryID: "6",
          SuperCategoryName: "Equipment",
          SupplierConfirmationNumber: "",
          SupplierId: "2022626",
          SupplierName: "Alpine Adventures Europe Supplier",
          TaxesFees: "0.0000",
          TotalNights: "0",
        },
        {
          AdultCount: "0",
          ArrivalDate: "2016-02-21T00:00:00",
          CCType: "  ",
          ChildCount: "1",
          CommissionBillDate: "2016-03-27T00:00:00",
          CommissionDueDate: "2016-04-30T00:00:00",
          CommissionRate: "0.2700",
          CommissionTotal: "20.0000",
          CreateDateTime: "2015-09-10T15:01:59.933",
          DateCancelled: "1900-01-01T00:00:00",
          DateSaved: "2015-09-09T14:00:54.097",
          DepartureDate: "2016-02-26T00:00:00",
          IATACommissionDueDate: "1900-01-01T00:00:00",
          IATACommissionRate: "0.00000000",
          IATACommissionTotal: "0",
          IsCRSPassThrough: "1",
          ItineraryId: "10204794",
          ItineraryItemId: "9",
          LastChange: "2018-05-08T17:15:00.620",
          LocationId: "2022626",
          LocationName: "Alpine Adventures Europe Supplier",
          LoggedAgentForItem: "igerlach",
          NetPrice: "55.0000",
          OrigNetPrice: "55.0000",
          OrigPrice: "75.0000",
          OrigTaxesFees: "0.0000",
          PackageId: "0",
          Price: "75.0000",
          PriorNetPrice: "0.0000",
          PriorPrice: "0.0000",
          PriorTaxesFees: "0.0000",
          ProductCategoryId: "1061",
          ProductCategoryName: "Ski Rental",
          ProductId: "837",
          ProductName: "6 Day Junior Ski Rental Pack with helmet",
          Quantity: "1",
          ResDate: "2015-09-28T10:41:40.567",
          ResellerCollects: "1",
          SalesCommissionBillDate: "2016-03-27T00:00:00",
          SalesCommissionDueDate: "2016-04-30T00:00:00",
          SalesCommissionRate: "0.2300",
          SalesCommissionTotal: "17.6000",
          StatusCode: "2",
          SuperCategoryID: "6",
          SuperCategoryName: "Equipment",
          SupplierConfirmationNumber: "",
          SupplierId: "2022626",
          SupplierName: "Alpine Adventures Europe Supplier",
          TaxesFees: "0.0000",
          TotalNights: "0",
        },
        {
          AdultCount: "1",
          ArrivalDate: "2016-02-21T00:00:00",
          CCType: "  ",
          ChildCount: "0",
          CommissionBillDate: "2016-03-22T00:00:00",
          CommissionDueDate: "2016-04-30T00:00:00",
          CommissionRate: "0.1300",
          CommissionTotal: "126.4800",
          CreateDateTime: "2015-09-25T10:03:51.297",
          DateCancelled: "1900-01-01T00:00:00",
          DateSaved: "2015-09-09T14:00:54.097",
          DepartureDate: "2016-02-21T00:00:00",
          IATACommissionDueDate: "1900-01-01T00:00:00",
          IATACommissionRate: "0.00000000",
          IATACommissionTotal: "0",
          IsCRSPassThrough: "1",
          ItineraryId: "10204794",
          ItineraryItemId: "10",
          LastChange: "2021-06-25T14:52:34.870",
          LocationId: "2070613",
          LocationName: "Val Thorens Lift Company ",
          LoggedAgentForItem: "igerlach",
          NetPrice: "839.7200",
          OrigNetPrice: "839.7200",
          OrigPrice: "1007.6800",
          OrigTaxesFees: "0.0000",
          PackageId: "0",
          Price: "966.2000",
          PriorNetPrice: "0.0000",
          PriorPrice: "0.0000",
          PriorTaxesFees: "0.0000",
          ProductCategoryId: "117",
          ProductCategoryName: "Lift Tickets",
          ProductId: "36",
          ProductName: "Family 3 Vallees lift ticket - 6 consecutive days",
          Quantity: "4",
          ResDate: "2015-09-28T10:41:40.577",
          ResellerCollects: "1",
          SalesCommissionBillDate: "2016-03-22T00:00:00",
          SalesCommissionDueDate: "2016-04-30T00:00:00",
          SalesCommissionRate: "0.1200",
          SalesCommissionTotal: "111.3024",
          StatusCode: "2",
          SuperCategoryID: "9",
          SuperCategoryName: "Lifts",
          SupplierConfirmationNumber: "",
          SupplierId: "2070613",
          SupplierName: "Val Thorens Lift Company ",
          TaxesFees: "0.0000",
          TotalNights: "0",
        },
        {
          AdultCount: "2",
          ArrivalDate: "2016-02-21T00:00:00",
          CCType: "  ",
          ChildCount: "2",
          CommissionBillDate: "2016-03-27T00:00:00",
          CommissionDueDate: "2016-04-30T00:00:00",
          CommissionRate: "0.0900",
          CommissionTotal: "150.0000",
          CreateDateTime: "2016-01-16T10:40:35.747",
          DateCancelled: "1900-01-01T00:00:00",
          DateSaved: "2015-09-09T14:00:54.097",
          DepartureDate: "2016-02-26T00:00:00",
          IATACommissionDueDate: "1900-01-01T00:00:00",
          IATACommissionRate: "0.00000000",
          IATACommissionTotal: "0",
          IsCRSPassThrough: "1",
          ItineraryId: "10204794",
          ItineraryItemId: "11",
          LastChange: "2018-05-08T17:38:01.400",
          LocationId: "2022626",
          LocationName: "Alpine Adventures Europe Supplier",
          LoggedAgentForItem: "igerlach",
          NetPrice: "1500.0000",
          OrigNetPrice: "1500.0000",
          OrigPrice: "1650.0000",
          OrigTaxesFees: "0.0000",
          PackageId: "0",
          Price: "1650.0000",
          PriorNetPrice: "0.0000",
          PriorPrice: "0.0000",
          PriorTaxesFees: "0.0000",
          ProductCategoryId: "1045",
          ProductCategoryName: "Ski/Snowboard Group Lessons",
          ProductId: "941",
          ProductName:
            "6 Days 1st level kids ski group lesson incl. lunch and 3 Days x 3 hours private ski guide for 2 Adul",
          Quantity: "1",
          ResDate: "2016-01-16T10:40:57.903",
          ResellerCollects: "1",
          SalesCommissionBillDate: "2016-03-27T00:00:00",
          SalesCommissionDueDate: "2016-04-30T00:00:00",
          SalesCommissionRate: "0.0800",
          SalesCommissionTotal: "132.0000",
          StatusCode: "2",
          SuperCategoryID: "16",
          SuperCategoryName: "Lessons",
          SupplierConfirmationNumber: "",
          SupplierId: "2022626",
          SupplierName: "Alpine Adventures Europe Supplier",
          TaxesFees: "0.0000",
          TotalNights: "0",
        },
        {
          AdultCount: "0",
          ArrivalDate: "2016-02-27T00:00:00",
          CCType: "  ",
          ChildCount: "0",
          CommissionBillDate: "2016-03-28T00:00:00",
          CommissionDueDate: "2016-04-30T00:00:00",
          CommissionRate: "0.0000",
          CommissionTotal: "0.0000",
          CreateDateTime: "2016-01-16T10:40:35.847",
          DateCancelled: "1900-01-01T00:00:00",
          DateSaved: "2015-09-09T14:00:54.097",
          DepartureDate: "2016-02-27T00:00:00",
          IATACommissionDueDate: "1900-01-01T00:00:00",
          IATACommissionRate: "0.00000000",
          IATACommissionTotal: "0",
          IsCRSPassThrough: "1",
          ItineraryId: "10204794",
          ItineraryItemId: "12",
          LastChange: "2016-01-16T10:40:57.960",
          LocationId: "1874224",
          LocationName: "Alpine Adventures - Anywhere Adventures",
          LoggedAgentForItem: "igerlach",
          NetPrice: "0.0000",
          OrigNetPrice: "0.0000",
          OrigPrice: "57.7500",
          OrigTaxesFees: "0.0000",
          PackageId: "0",
          Price: "57.7500",
          PriorNetPrice: "0.0000",
          PriorPrice: "0.0000",
          PriorTaxesFees: "0.0000",
          ProductCategoryId: "110",
          ProductCategoryName: "Booking Fee",
          ProductId: "1",
          ProductName: "Thank you for booking with Alpine Adventures",
          Quantity: "1",
          ResDate: "2016-01-16T10:40:57.960",
          ResellerCollects: "1",
          SalesCommissionBillDate: "2016-03-28T00:00:00",
          SalesCommissionDueDate: "2016-04-30T00:00:00",
          SalesCommissionRate: "0.0000",
          SalesCommissionTotal: "0.0000",
          StatusCode: "2",
          SuperCategoryID: "10",
          SuperCategoryName: "Misc",
          SupplierConfirmationNumber: "",
          SupplierId: "1874224",
          SupplierName: "Alpine Adventures - Anywhere Adventures",
          TaxesFees: "0.0000",
          TotalNights: "0",
        },
      ]

      // name: `${Customer.GivenName} ${
      //   Customer.MiddleName && Customer.MiddleName
      // } ${Customer.Surname && Customer.Surname}`.trim(),

      onTemplate({
        name: Customer.ItineraryId,
        code: Number(Customer.ItineraryId),
        startDate: formatDate(
          minDate(ItineraryItem.map((_) => parseISO(_.ArrivalDate))),
          "yyyy-MM-dd",
        ),
        endDate: formatDate(
          maxDate(ItineraryItem.map((_) => parseISO(_.DepartureDate))),
          "yyyy-MM-dd",
        ),

        products: ItineraryItem?.map((_, index) => ({
          name: _.ProductName,
          description: "",
          category: _.ProductCategoryName,
          availability: parseInt(_.Quantity),
          quantity: parseInt(_.Quantity),
          price: Number(_.Price),
          index,
        })),
      })
    }
  }, [template])

  const client = useApolloClient()

  const [isLoading, setIsLoading] = useState(true)

  const loadOptions = useCallback(
    async (inputValue) => {
      setIsLoading(true)

      const ITINARERIES = gql`
        query InttopiaItineraries(
          $filter: IndividualTripFilterInput
          $page: Int
          $size: Int
          $sort: IndividualTripSortInput
        ) {
          inttopiaItineraries(
            filter: $filter
            page: $page
            size: $size
            sort: $sort
          )
        }
      `

      try {
        const search = inputValue?.toLowerCase()

        const { data } = await client.query({
          variables: {
            page: 0,
            size: 50,
            // sort: { displayName: "ASC" },
            ...(search ? { filter: { search } } : {}),
          },
          query: ITINARERIES,
        })

        setIsLoading(false)

        return data?.inttopiaItineraries?.map((contract) => ({
          // label: contract.ItineraryId,
          label: `${contract.ItineraryId} (${contract.Customer.CreateDate})`,
          value: { ...contract },
        }))
      } catch (error) {}
    },
    [setIsLoading],
  )

  return (
    <>
      <div className="px-2 py-3 mt-2">
        <Fieldset
          async
          name="template"
          component="select"
          label="Itinerary *"
          isLoading={isLoading}
          loadOptions={loadOptions}
          placeholder="Select intopia itinerary"
        />
      </div>

      <div className="my-10 flex items-center space-x-4 w-full">
        <p>Or</p>
        <div className="w-full border-t-2 border-gray-200" />
      </div>
    </>
  )
}

const TripGeneralForm = () => {
  return (
    <div>
      <div className="py-3 flex justify-between">
        {/* <div className="px-2 w-8/12">
          <Fieldset
            name="name"
            label="Name *"
            type="text"
            placeholder="Enter trip name"
            maxlength={35}
          />
        </div> */}

        <div className="px-2 w-4/12">
          <Fieldset
            name="code"
            label="ID *"
            type="number"
            placeholder="Enter trip ID"
          />
        </div>
      </div>

      <div className="py-3 flex justify-between">
        <div className="px-2 flex-1">
          <Fieldset
            name="startDate"
            label="Start date *"
            type="date"
            // min={startDateMin}
          />
        </div>

        <div className="px-2 flex-1">
          <Fieldset
            name="endDate"
            label="End date *"
            type="date"
            // min={endDateMin}
          />
        </div>
      </div>

      <div className="px-2 py-3">
        <FieldDestinations />
      </div>
    </div>
  )
}

const TripLegalForm = () => {
  const CONTRACTS = gql`
    query Contracts($page: Int, $size: Int) {
      contracts(page: $page, size: $size) {
        id
        name
      }
    }
  `

  const { data, loading } = useQuery(CONTRACTS, {
    variables: { page: 0, size: 100 },
  })

  const contracts = data?.contracts?.map((contract) => ({
    label: contract.name,
    value: { ...contract },
  }))

  return (
    <div className="w-full mt-8">
      <div className="px-2 py-3 mt-2">
        <Fieldset
          component="select"
          label="Contract *"
          label="Terms and Conditions *"
          name="legal.contract"
          isLoading={loading}
          mapping={{ label: "name" }}
          options={contracts}
          placeholder="Select trip contract"
        />
      </div>
    </div>
  )
}

const TripOrganisationForm = () => {
  const USERS = gql`
    query Users(
      $filter: UserFilterInput
      $page: Int
      $size: Int
      $sort: UserSortInput
    ) {
      users(filter: $filter, page: $page, size: $size, sort: $sort) {
        id
        displayName
        email
        photoURL
      }
    }
  `

  const variables = {
    page: 0,
    size: 20,
    filter: { roles: ["ADMIN", "AGENT"] },
    sort: { displayName: "ASC" },
  }

  const { data, loading, fetchMore } = useQuery(USERS, {
    variables: {
      page: 0,
      size: 10,
      filter: { roles: ["ADMIN", "AGENT"] },
      sort: { displayName: "ASC" },
    },
    notifyOnNetworkStatusChange: true,
  })

  const users = data?.users?.map((user) => ({
    key: user.id,
    label: user.displayName,
    value: { ...user },
  }))

  const handleSearch = (_search, e) => {
    if (e.action === "input-change") {
      const search = _search?.toLowerCase()
      fetchMore({
        variables: { ...variables, filter: { ...variables.filter, search } },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev
          return Object.assign({}, prev, {
            users: uniqBy(
              [...prev.users, ...fetchMoreResult.users],
              "displayName",
            ),
          })
        },
      })
    }
  }

  return (
    <div className="w-full mt-16">
      <div className="px-2 py-3 mt-2">
        <Fieldset
          name="account"
          label="Account (Payment) *"
          component="select"
          options={[
            { label: "Alpine Adventures", value: "ALPINE_ADVENTURES" },
            { label: "Adventures Africa", value: "ADVENTURES_AFRICA" },
          ]}
          placeholder="Select trip account"
        />
      </div>

      <div className="px-2 py-3 mt-2">
        <FieldAgency />
      </div>

      <div className="px-2 py-3 mt-2">
        <Fieldset
          name="agents"
          label="Agents *"
          component="select"
          options={users}
          cacheOptions
          isMulti
          isLoading={loading}
          onInputChange={handleSearch}
          getOptionValue={(opt) => opt.key}
          mapping={{ label: "displayName" }}
          placeholder="Select trip agents"
        />
      </div>
    </div>
  )
}

//
//
//
//

const FieldAgency = () => {
  const AGENCIES = gql`
    query Agencies(
      $filter: AgencyFilterInput
      $page: Int
      $size: Int
      $sort: AgencySortInput
    ) {
      agencies(filter: $filter, page: $page, size: $size, sort: $sort) {
        id
        name
        description
        logoURL
      }
    }
  `

  const variables = {
    page: 0,
    size: 10,
    // filter: { roles: ["ADMIN", "AGENT", "LEADER"] },
    // sort: { displayName: "ASC" },
  }

  const { data, loading, fetchMore } = useQuery(AGENCIES, {
    variables,
    notifyOnNetworkStatusChange: true,
  })

  const agencies = data?.agencies?.map((agency) => ({
    key: agency.id,
    label: agency.name,
    value: { ...agency },
  }))

  const handleSearch = (_search, e) => {
    if (e.action === "input-change") {
      const search = _search?.toLowerCase()
      fetchMore({
        variables: { ...variables, filter: { ...variables?.filter, search } },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev
          return Object.assign({}, prev, {
            agencies: uniqBy(
              [...prev.agencies, ...fetchMoreResult.agencies],
              "name",
            ),
          })
        },
      })
    }
  }

  return (
    <Fieldset
      name="agency"
      label="Agency *"
      component="select"
      options={agencies}
      onInputChange={handleSearch}
      getOptionValue={(opt) => opt.key}
      mapping={{ label: "name" }}
      placeholder="Select trip agency"
    />
  )
}

const FieldDestinations = () => {
  const [countries, setCountries] = useState(null)
  const [options, setOptions] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  const loadCountries = async () => {
    try {
      const countries = await fetch("/countries.json").then((_) => _.json())
      setCountries(countries)
      setOptions(countries)
      setIsLoading(false)
    } catch (error) {}
  }

  useEffect(() => {
    if (!countries) {
      loadCountries()
    }
  }, [countries])

  const handleSearch = (search, e) => {
    setIsLoading(true)
    if (!search) {
      setOptions(countries)
      setIsLoading(false)
      return
    }

    if (e.action === "input-change") {
      // links[j].text.indexOf(queryTerms[i])
      // countries.contains(search)
      setOptions(null)

      const found = countries
        .filter((_) => _.name.toLowerCase().indexOf(search.toLowerCase()) > -1)
        .map((_) => ({
          ..._,
          score: _.name.toLowerCase().indexOf(search.toLowerCase()),
        }))
        .sort((a, b) => a.score - b.score)
      setOptions(found)
    }

    setIsLoading(false)
  }

  const _options = options?.map((_) => ({
    label: _.name,
    value: { ..._ },
  }))

  return (
    <Fieldset
      name="destinations"
      label="Destinations (Countries) *"
      placeholder="Enter trip destinations"
      component="select"
      options={_options}
      cacheOptions
      isMulti
      isLoading={isLoading}
      onInputChange={handleSearch}
      getOptionValue={(opt) => opt.label}
      mapping={{ label: "name" }}
    />
  )
}

const FieldDeposit = ({ getIndex, data, endDateMax, arrayHelpers }) => {
  return (
    <div className="flex flex-col">
      {data?.map((part, index) => (
        <>
          <div
            key={index}
            className={`flex justify-between items-center my-4 px-6 space-x-2`}
          >
            <div className="w-3/12 text-base capitalize">
              <label>
                {index + 1}
                {/* {data.length === 1 || index !== data.length - 1
                  ? `${stringifyNumber(index + 1)} deposit`
                  : "Final payment"} */}
              </label>
            </div>

            <div className="w-3/12">
              <Fieldset name={`deposits[${index}].amount`} type="number" />
            </div>

            <div className="w-4/12 flex justify-between items-center">
              <Fieldset
                name={`deposits[${index}].dueDate`}
                type="date"
                // min={TODAY}
                // max={endDateMax}
              />
            </div>

            <div className="w-1/12 flex justify-end items-center">
              <button
                type="button"
                className="inline-block"
                onClick={() => arrayHelpers.remove(index)}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9 2C8.62123 2 8.27497 2.214 8.10557 2.55279L7.38197 4H4C3.44772 4 3 4.44772 3 5C3 5.55228 3.44772 6 4 6L4 16C4 17.1046 4.89543 18 6 18H14C15.1046 18 16 17.1046 16 16V6C16.5523 6 17 5.55228 17 5C17 4.44772 16.5523 4 16 4H12.618L11.8944 2.55279C11.725 2.214 11.3788 2 11 2H9ZM7 8C7 7.44772 7.44772 7 8 7C8.55228 7 9 7.44772 9 8V14C9 14.5523 8.55228 15 8 15C7.44772 15 7 14.5523 7 14V8ZM12 7C11.4477 7 11 7.44772 11 8V14C11 14.5523 11.4477 15 12 15C12.5523 15 13 14.5523 13 14V8C13 7.44772 12.5523 7 12 7Z"
                    fill="#4A5568"
                  />
                </svg>
              </button>
            </div>
          </div>
        </>
      ))}
    </div>
  )
}

const FormNavigation = ({ isSubmitting }) => {
  return (
    <nav className="mt-6 py-3 sm:flex sm:flex-row-reverse">
      <span className="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
        <button
          disabled={isSubmitting}
          type="submit"
          className="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-green transition ease-in-out duration-150 sm:text-sm sm:leading-5 disabled:opacity-50 disabled:cursor-wait"
        >
          Create
        </button>
      </span>

      {/* {showBack && (
        <span className="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
          <button
            onClick={goBack}
            type="button"
            className="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150 sm:text-sm sm:leading-5"
          >
            Back
          </button>
        </span>
      )} */}
    </nav>
  )
}
